import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
} from '@mui/material';
import Home from '@mui/icons-material/Home';
import MenuBook from '@mui/icons-material/MenuBook';
import TipsAndUpdatesOutlined from '@mui/icons-material/TipsAndUpdatesOutlined';
import Download from '@mui/icons-material/Download';
import Forum from '@mui/icons-material/Forum';
import PieChartIcon from '@mui/icons-material/PieChart';
import Dashboard from '@mui/icons-material/Dashboard';
import AutoStories from '@mui/icons-material/AutoStories';
import { useTranslation } from 'react-i18next';
import { useLocaleHandler } from '../../hooks';
import { EXTERNAL_LINKS } from '../../constants/config';
import {
  appBarSx,
  BurgerMenu,
  LanguageSelector,
  NavButton,
  SidebarToggle,
} from './header';

/**
 * Ширина экрана, с которой у пунктов центральной навигации появляются подписи.
 * Ниже неё — только иконки.
 *
 * Число НЕ на глаз, оно считается. Центральный блок лежит `position: absolute` по центру,
 * поэтому соседей он не расталкивает, а **молча наезжает** на них (общая ловушка, см.
 * `docs/responsive.md`). Условие «не наезжает»:
 *
 *   правый край навигации  = W/2 + Wнав/2
 *   левый край правого блока = W − padding − Wправ
 *   W >= Wнав + 2 * (padding + Wправ + зазор)
 *
 * Мерить надо ХУДШУЮ ЛОКАЛЬ, а не русскую: подписи в разных языках разной длины, и самой
 * широкой оказалась украинская, а не та, в которой обычно смотрят.
 *
 * | локаль | Wнав | Wправ |
 * |--------|------|-------|
 * | ru     |  976 |  292  |
 * | en     |  994 |  291  |
 * | uk     |  995 |  309  |  ← считаем по ней
 *
 * При `padding = 24` и зазоре 48px формула даёт 1757 → округляем до **1760**.
 * Фактический зазор на самой границе — 49px, на 1920px — 134px. Ниже порога навигация
 * ужимается до 376px, и до правого блока остаётся больше 300px.
 *
 * ⚠️ Число придётся пересчитать, если добавить/убрать пункт навигации ИЛИ изменить подпись
 * в любой из трёх локалей. На этом уже дважды обожглись: сначала здесь стояло 1650 под шесть
 * пунктов и с седьмым на 1667px оставалось 6px; потом порог посчитали только по русской
 * локали — для украинской он был мал.
 */
const NAV_LABELS_FROM = 1760;

/**
 * Код кнопки рейтинга l2oops. Выдан рейтингом, ставить обязаны ДОСЛОВНО — поэтому строкой, а не
 * JSX: в DOM ровно их разметка, включая alt/title со словом «Lineage» (исключение из
 * docs/ui-patterns.md §8). Не править, не уменьшать, не заменять своим логотипом — уже пробовали.
 */
const L2OOPS_BUTTON_HTML =
  '<a href="https://l2oops.com" style="z-index:99999;" target="_blank"><img src="https://l2oops.com/logo/black_l2oops_mini.png" width="122" height="31" alt="Новые сервера Lineage 2" title="Новые сервера Lineage 2" border="0"></a>';

interface PublicHeaderProps {
  onDrawerToggle?: () => void;
  /** Сайдбар свёрнут (десктоп). Без обработчика кнопки нет — на страницах без сайдбара она не нужна. */
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  /** Кнопка рейтинга l2oops слева. Включается только в маршруте главной (`/` в App.tsx). */
  showRatingBanner?: boolean;
}

const PublicHeader: React.FC<PublicHeaderProps> = ({
  onDrawerToggle,
  sidebarCollapsed = false,
  onToggleSidebar,
  showRatingBanner,
}) => {
  const { t } = useTranslation('navigation');
  const { currentLocale, changingLocale, handleLocaleChange } = useLocaleHandler();

  // Scroll state for glass effect
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let frameId: number | null = null;
    const handleScroll = () => {
      if (frameId === null) {
        frameId = requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          frameId = null;
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        ...appBarSx,
        transition: 'background 0.3s ease, backdrop-filter 0.3s ease, box-shadow 0.3s ease',
        ...(isScrolled && {
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        }),
      }}
    >
      <Toolbar>
        <BurgerMenu onDrawerToggle={onDrawerToggle} />
        <SidebarToggle collapsed={sidebarCollapsed} onToggle={onToggleSidebar} />

        {/* Кнопка рейтинга l2oops (L2OOPS_BUTTON_HTML), только на десктопе. Слева: там пусто,
            а кнопка (122px) уже правого блока, так что порог NAV_LABELS_FROM она не двигает.
            Разметку не трогаем — только CSS обёртки. */}
        {showRatingBanner && (
          <Box
            dangerouslySetInnerHTML={{ __html: L2OOPS_BUTTON_HTML }}
            sx={{
              // Порог 870px — тот же рубеж, на котором шапка переключается с бургера на полную
              // навигацию. Ниже кнопки нет: на телефонах её роль играет копия на промо-лендинге.
              display: 'none',
              flexShrink: 0,
              // img в их коде строчный: без block под ним остаётся место под свисающие буквы,
              // и кнопка встаёт выше соседей (см. docs/responsive.md про бургер)
              '& img': { display: 'block' },
              '@media (min-width: 870px)': { display: 'flex' },
            }}
          />
        )}

        {/* Central navigation */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'none',
            '@media (min-width: 870px)': { display: 'flex' },
            gap: '8px',
            alignItems: 'center',
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            '@media (min-width: 1280px)': { gap: '16px' },
          }}
        >
          <NavButton
            icon={<Home />}
            label={t('footer.home')}
            to="/"
            breakpoint={NAV_LABELS_FROM}
          />
          <NavButton
            icon={<MenuBook />}
            label={t('menu.wiki')}
            to="/wiki"
            breakpoint={NAV_LABELS_FROM}
          />
          <NavButton
            icon={<TipsAndUpdatesOutlined />}
            label={t('footer.guide')}
            to="/guide"
            breakpoint={NAV_LABELS_FROM}
          />
          <NavButton
            icon={<AutoStories />}
            label={t('footer.posts')}
            to="/posts"
            breakpoint={NAV_LABELS_FROM}
          />
          <NavButton
            icon={<Download />}
            label={t('footer.download')}
            to="/downloads"
            breakpoint={NAV_LABELS_FROM}
          />
          <NavButton
            icon={<Forum />}
            label={t('footer.community')}
            href={EXTERNAL_LINKS.TELEGRAM_CHAT}
            breakpoint={NAV_LABELS_FROM}
          />
          <NavButton
            icon={<PieChartIcon />}
            label={t('footer.raceBalance')}
            to="/race-balance"
            breakpoint={NAV_LABELS_FROM}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Right side with language switcher and auth button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LanguageSelector
            currentLocale={currentLocale}
            changingLocale={changingLocale}
            onLocaleChange={handleLocaleChange}
          />

          {/* Personal Cabinet Button */}
          <NavButton
            icon={<Dashboard />}
            label={t('toolbar.personalCabinet')}
            to="/login"
            breakpoint={1250}
          />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default PublicHeader;
